import { Contract, Coin, TxnStdFee } from "./contract";
import { SecretNetworkClient } from "secretjs";

export type ExecuteMsg = {
  update_config: {
    checkpoint_generator_limit?: number | null;
    generator_controller?: string | null;
    generator_hash?: string | null;
    guardian?: string | null;
    vesting_contract?: string | null;
    vesting_contract_hash?: string | null;
    voting_escrow?: string | null;
    voting_escrow_delegation?: string | null;
    voting_escrow_delegation_hash?: string | null;
    voting_escrow_hash?: string | null;
    [k: string]: unknown;
  };
} | {
  setup_pools: {
    pools: [[string, string], Uint128][];
    [k: string]: unknown;
  };
} | {
  update_pool: {
    has_asset_rewards: boolean;
    lp_token: string;
    [k: string]: unknown;
  };
} | {
  claim_rewards: {
    lp_tokens: [Addr, string][];
    [k: string]: unknown;
  };
} | {
  withdraw: {
    amount: Uint128;
    lp_token: [Addr, string];
    [k: string]: unknown;
  };
} | {
  emergency_withdraw: {
    lp_token: [string, string];
    [k: string]: unknown;
  };
} | {
  receive: Snip20ReceiveMsg;
} | {
  set_tokens_per_block: {
    amount: Uint128;
    [k: string]: unknown;
  };
} | {
  update_blocked_tokenslist: {
    add?: AssetInfo[] | null;
    remove?: AssetInfo[] | null;
    [k: string]: unknown;
  };
} | {
  deactivate_pool: {
    lp_token: string;
    [k: string]: unknown;
  };
} | {
  deactivate_pools: {
    pair_types: string[];
    [k: string]: unknown;
  };
} | {
  checkpoint_user_boost: {
    generators: [string, string][];
    user?: string | null;
    [k: string]: unknown;
  };
} | {
  callback: {
    action: ExecuteOnReply;
    [k: string]: unknown;
  };
};
export type Uint128 = string;
export type Addr = string;
export type Binary = string;
export type ExecuteOnReply = {
  ClaimRewards: {
    userAddress: Addr;
    lp_tokens: [Addr, string][];
    [k: string]: unknown;
  };
} | {
  Deposit: {
    userAddress: Addr;
    amount: Uint128;
    lp_token: [Addr, string];
    [k: string]: unknown;
  };
} | {
  Withdraw: {
    userAddress: Addr;
    amount: Uint128;
    lp_token: [Addr, string];
    [k: string]: unknown;
  };
} | {
  SetTokensPerBlock: {
    amount: Uint128;
    [k: string]: unknown;
  };
};
export interface Snip20ReceiveMsg {
  amount: Uint128;
  from: Addr;
  memo?: string | null;
  msg?: Binary | null;
  sender: Addr;
  [k: string]: unknown;
}
export interface AssetInfo {
  contract_addr?: Addr | null;
  contract_code_hash?: string | null;
  denom?: string | null;
  [k: string]: unknown;
}
export type Uint64 = string;
export interface InstantiateMsg {
  allowed_reward_proxies: string[];
  astro_hash: string;
  astro_token: string;
  factory: string;
  factory_hash: string;
  generator_controller?: string | null;
  generator_controller_hash?: string | null;
  guardian?: string | null;
  owner: string;
  start_block: Uint64;
  tokens_per_block: Uint128;
  vesting_contract: string;
  vesting_contract_hash: string;
  voting_escrow?: string | null;
  voting_escrow_delegation?: string | null;
  voting_escrow_delegation_hash?: string | null;
  voting_escrow_hash?: string | null;
  whitelist_code_id: number;
  [k: string]: unknown;
}
export type QueryMsg = {
  active_pool_length: {
    [k: string]: unknown;
  };
} | {
  pool_length: {
    [k: string]: unknown;
  };
} | {
  deposit: {
    lp_token: string;
    user: string;
    [k: string]: unknown;
  };
} | {
  user_virtual_amount: {
    lp_token: string;
    user: string;
    [k: string]: unknown;
  };
} | {
  total_virtual_supply: {
    generator: string;
    [k: string]: unknown;
  };
} | {
  pending_token: {
    lp_token: string;
    user: string;
    [k: string]: unknown;
  };
} | {
  config: {
    [k: string]: unknown;
  };
} | {
  reward_info: {
    lp_token: string;
    [k: string]: unknown;
  };
} | {
  pool_info: {
    lp_token: [string, string];
    [k: string]: unknown;
  };
} | {
  simulate_future_reward: {
    future_block: number;
    lp_token: string;
    [k: string]: unknown;
  };
} | {
  blocked_tokens_list: {
    [k: string]: unknown;
  };
};
export interface SecretswapGeneratorReadOnlyInterface {
  activePoolLength: () => Promise<any>;
  poolLength: () => Promise<any>;
  deposit: ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }) => Promise<any>;
  userVirtualAmount: ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }) => Promise<any>;
  totalVirtualSupply: ({
    generator
  }: {
    generator: string;
  }) => Promise<any>;
  pendingToken: ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }) => Promise<any>;
  config: () => Promise<any>;
  rewardInfo: ({
    lpToken
  }: {
    lpToken: string;
  }) => Promise<any>;
  poolInfo: ({
    lpToken
  }: {
    lpToken: [string, string];
  }) => Promise<any>;
  simulateFutureReward: ({
    futureBlock,
    lpToken
  }: {
    futureBlock: number;
    lpToken: string;
  }) => Promise<any>;
  blockedTokensList: () => Promise<any>;
}
export class SecretswapGeneratorQueryContract extends Contract implements SecretswapGeneratorReadOnlyInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.activePoolLength = this.activePoolLength.bind(this);
    this.poolLength = this.poolLength.bind(this);
    this.deposit = this.deposit.bind(this);
    this.userVirtualAmount = this.userVirtualAmount.bind(this);
    this.totalVirtualSupply = this.totalVirtualSupply.bind(this);
    this.pendingToken = this.pendingToken.bind(this);
    this.config = this.config.bind(this);
    this.rewardInfo = this.rewardInfo.bind(this);
    this.poolInfo = this.poolInfo.bind(this);
    this.simulateFutureReward = this.simulateFutureReward.bind(this);
    this.blockedTokensList = this.blockedTokensList.bind(this);
  }

  activePoolLength = async (): Promise<any> => {
    return this.queryMsg({
      active_pool_length: {}
    });
  };
  poolLength = async (): Promise<any> => {
    return this.queryMsg({
      pool_length: {}
    });
  };
  deposit = async ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }): Promise<any> => {
    return this.queryMsg({
      deposit: {
        lp_token: lpToken,
        user
      }
    });
  };
  userVirtualAmount = async ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }): Promise<any> => {
    return this.queryMsg({
      user_virtual_amount: {
        lp_token: lpToken,
        user
      }
    });
  };
  totalVirtualSupply = async ({
    generator
  }: {
    generator: string;
  }): Promise<any> => {
    return this.queryMsg({
      total_virtual_supply: {
        generator
      }
    });
  };
  pendingToken = async ({
    lpToken,
    user
  }: {
    lpToken: string;
    user: string;
  }): Promise<any> => {
    return this.queryMsg({
      pending_token: {
        lp_token: lpToken,
        user
      }
    });
  };
  config = async (): Promise<any> => {
    return this.queryMsg({
      config: {}
    });
  };
  rewardInfo = async ({
    lpToken
  }: {
    lpToken: string;
  }): Promise<any> => {
    return this.queryMsg({
      reward_info: {
        lp_token: lpToken
      }
    });
  };
  poolInfo = async ({
    lpToken
  }: {
    lpToken: [string, string];
  }): Promise<any> => {
    return this.queryMsg({
      pool_info: {
        lp_token: lpToken
      }
    });
  };
  simulateFutureReward = async ({
    futureBlock,
    lpToken
  }: {
    futureBlock: number;
    lpToken: string;
  }): Promise<any> => {
    return this.queryMsg({
      simulate_future_reward: {
        future_block: futureBlock,
        lp_token: lpToken
      }
    });
  };
  blockedTokensList = async (): Promise<any> => {
    return this.queryMsg({
      blocked_tokens_list: {}
    });
  };
}
export interface SecretswapGeneratorInterface extends SecretswapGeneratorReadOnlyInterface {
  updateConfig: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    checkpointGeneratorLimit,
    generatorController,
    generatorHash,
    guardian,
    vestingContract,
    vestingContractHash,
    votingEscrow,
    votingEscrowDelegation,
    votingEscrowDelegationHash,
    votingEscrowHash
  }: {
    checkpointGeneratorLimit?: number;
    generatorController?: string;
    generatorHash?: string;
    guardian?: string;
    vestingContract?: string;
    vestingContractHash?: string;
    votingEscrow?: string;
    votingEscrowDelegation?: string;
    votingEscrowDelegationHash?: string;
    votingEscrowHash?: string;
  }) => Promise<any>;
  updatePool: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    hasAssetRewards,
    lpToken
  }: {
    hasAssetRewards: boolean;
    lpToken: string;
  }) => Promise<any>;
  receive: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
  setTokensPerBlock: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount
  }: {
    amount: Uint128;
  }) => Promise<any>;
  updateBlockedTokenslist: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    add,
    remove
  }: {
    add?: AssetInfo[];
    remove?: AssetInfo[];
  }) => Promise<any>;
  deactivatePool: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    lpToken
  }: {
    lpToken: string;
  }) => Promise<any>;
  deactivatePools: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairTypes
  }: {
    pairTypes: string[];
  }) => Promise<any>;
  callback: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    action
  }: {
    action: ExecuteOnReply;
  }) => Promise<any>;
}
export class SecretswapGeneratorContract extends SecretswapGeneratorQueryContract implements SecretswapGeneratorInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.updateConfig = this.updateConfig.bind(this);
    // this.setupPools = this.setupPools.bind(this);
    this.updatePool = this.updatePool.bind(this);
    this.claimRewards = this.claimRewards.bind(this);
    this.withdraw = this.withdraw.bind(this);
    // this.emergencyWithdraw = this.emergencyWithdraw.bind(this);
    this.receive = this.receive.bind(this);
    this.setTokensPerBlock = this.setTokensPerBlock.bind(this);
    this.updateBlockedTokenslist = this.updateBlockedTokenslist.bind(this);
    this.deactivatePool = this.deactivatePool.bind(this);
    this.deactivatePools = this.deactivatePools.bind(this);
    // this.checkpointUserBoost = this.checkpointUserBoost.bind(this);
    this.callback = this.callback.bind(this);
  }

  updateConfig = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    checkpointGeneratorLimit,
    generatorController,
    generatorHash,
    guardian,
    vestingContract,
    vestingContractHash,
    votingEscrow,
    votingEscrowDelegation,
    votingEscrowDelegationHash,
    votingEscrowHash
  }: {
    checkpointGeneratorLimit?: number;
    generatorController?: string;
    generatorHash?: string;
    guardian?: string;
    vestingContract?: string;
    vestingContractHash?: string;
    votingEscrow?: string;
    votingEscrowDelegation?: string;
    votingEscrowDelegationHash?: string;
    votingEscrowHash?: string;
  }): Promise<any> => {
    return await this.executeMsg({
      update_config: {
        checkpoint_generator_limit: checkpointGeneratorLimit,
        generator_controller: generatorController,
        generator_hash: generatorHash,
        guardian,
        vesting_contract: vestingContract,
        vesting_contract_hash: vestingContractHash,
        voting_escrow: votingEscrow,
        voting_escrow_delegation: votingEscrowDelegation,
        voting_escrow_delegation_hash: votingEscrowDelegationHash,
        voting_escrow_hash: votingEscrowHash
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  updatePool = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    hasAssetRewards,
    lpToken
  }: {
    hasAssetRewards: boolean;
    lpToken: string;
  }): Promise<any> => {
    return await this.executeMsg({
      update_pool: {
        has_asset_rewards: hasAssetRewards,
        lp_token: lpToken
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  claimRewards = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    lpTokens
  }: {
    lpTokens: [Addr, string][];
  }): Promise<any> => {
    return await this.executeMsg({
      claim_rewards: {
        lp_tokens: lpTokens
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  withdraw = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount,
    lpToken
  }: {
    amount: string;
    lpToken: [Addr, string];
  }): Promise<any> => {
    return await this.executeMsg({
      withdraw: {
        amount: amount,
        lp_token: lpToken,
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  receive = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      receive: {}
    }, userAddress, customFees, memo, transferAmount);
  };
  setTokensPerBlock = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount
  }: {
    amount: Uint128;
  }): Promise<any> => {
    return await this.executeMsg({
      set_tokens_per_block: {
        amount
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  updateBlockedTokenslist = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    add,
    remove
  }: {
    add?: AssetInfo[];
    remove?: AssetInfo[];
  }): Promise<any> => {
    return await this.executeMsg({
      update_blocked_tokenslist: {
        add,
        remove
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  deactivatePool = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    lpToken
  }: {
    lpToken: string;
  }): Promise<any> => {
    return await this.executeMsg({
      deactivate_pool: {
        lp_token: lpToken
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  deactivatePools = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairTypes
  }: {
    pairTypes: string[];
  }): Promise<any> => {
    return await this.executeMsg({
      deactivate_pools: {
        pair_types: pairTypes
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  callback = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    action
  }: {
    action: ExecuteOnReply;
  }): Promise<any> => {
    return await this.executeMsg({
      callback: {
        action
      }
    }, userAddress, customFees, memo, transferAmount);
  };
}